// Datenzugriff des Freelancer-Portals (ERP/CRM-Plan Abschnitt 7 „Workflow"
// Schritte 2–3 und 3.4 — Phase D3).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe und das
// snake_case<->camelCase-Mapping (Stil wie assignmentService.ts:
// async, throw bei error, Mapper oben). Die Fachlichkeit — Zugangszustand,
// Buendelung, Ampel — steht pur und getestet in freelancerPortalLogic.ts.
//
// ─── Was dieser Service NICHT tut ───────────────────────────────────────────
// Er filtert nicht auf „meine" Zeilen. Das waere ein Sicherheitsversprechen,
// das ein Client nicht halten kann. Die Begrenzung leistet die RLS aus
// supabase/freelancer_portal.sql (MIGRATIONS.md Nr. 20): ein Freelancer SIEHT
// nur seine Zeilen, egal wie die Abfrage aussieht. Ein zusaetzlicher
// Client-Filter wuerde nur verschleiern, wo die Entscheidung faellt.
//
// ─── Schreiben ausschliesslich ueber die RPC ────────────────────────────────
// Es gibt hier keinen einzigen insert/update/delete. Der Freelancer hat auf
// KEINER Tabelle eine permissive Schreib-Policy (freelancer_portal.sql,
// Abschnitt 5); der einzige Schreibweg ist `set_assignment_status`, und die
// prueft „eigener Auftrag" und die Uebergangsmatrix im Rumpf.
//
// Projekte und Begehungsdaten kommen ueber SECURITY-DEFINER-RPCs und NIE ueber
// `projects` oder eine der acht Kindtabellen direkt (Plan 3.4, Befund 3.3 —
// „Spaltenbegrenzung ueber Views/RPCs"). Seit D4 gilt das auch fuer die
// Projektliste: `freelancer_list_projects` hat die View `freelancer_projects_v`
// abgeloest, weil die eine offene SELECT-Policy auf `projects` brauchte
// (Begruendung bei fetchMeineProjekte).

import { supabase } from './supabaseClient';
import { uploadAsset } from './cloudService';
import { describeError } from './errorMessage';
import { AssignmentStatus, InspectionAssignment } from './assignmentLogic';
import { Freelancer, FreelancerQualification } from './freelancerLogic';
import { PortalProjekt } from './freelancerPortalLogic';
import {
    FreelancerInspectionData,
    InspectionPayload,
    inspectionFotoPfad,
    mapInspectionResponse,
} from './freelancerInspectionLogic';

/** Name der Spalten-View. Eine Konstante, damit der Name genau einmal steht. */
export const PORTAL_PROJEKT_VIEW = 'freelancer_projects_v';

/**
 * Genau die Spalten, die die View fuehrt — und dieselben, die
 * `freelancer_list_projects` als jsonb liefert.
 *
 * Seit D4 liest das Portal die Projekte ueber die RPC (Begruendung bei
 * fetchMeineProjekte); die Konstante bleibt als DOKUMENT der zulaessigen
 * Spaltenauswahl bestehen und wird von der Negativsuite
 * (scripts/rls-freelancer-check.ts) beim View-Zugriff verwendet. Wer sie
 * erweitert, muss die View UND die RPC mitaendern.
 */
export const PORTAL_PROJEKT_SPALTEN =
    'id, name, project_number, project_address, coordinator_name';

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

const mapProjektRow = (row: any): PortalProjekt => ({
    id: String(row.id),
    name: String(row.name || 'Unbenanntes Projekt'),
    projectNumber: row.project_number ?? undefined,
    address: row.project_address ?? undefined,
    coordinatorName: row.coordinator_name ?? undefined,
});

const mapAssignmentRow = (row: any): InspectionAssignment => ({
    id: row.id,
    companyId: row.company_id,
    freelancerId: row.freelancer_id,
    projectId: row.project_id,
    zeitraumVon: row.zeitraum_von,
    zeitraumBis: row.zeitraum_bis,
    verguetungCents: Number(row.verguetung_cents ?? 0),
    status: row.status,
    reportId: row.report_id ?? undefined,
    createdAt: row.created_at,
});

const mapFreelancerRow = (row: any): Freelancer => ({
    id: row.id,
    companyId: row.company_id,
    profileUserId: row.profile_user_id ?? undefined,
    name: row.name,
    firma: row.firma ?? undefined,
    street: row.street ?? undefined,
    zip: row.zip ?? undefined,
    city: row.city ?? undefined,
    lat: row.lat != null ? Number(row.lat) : undefined,
    lng: row.lng != null ? Number(row.lng) : undefined,
    einsatzradiusKm: Number(row.einsatzradius_km ?? 50),
    satzJeBegehungCents:
        row.satz_je_begehung_cents != null ? Number(row.satz_je_begehung_cents) : undefined,
    steuersatzBp: Number(row.steuersatz_bp ?? 1900),
    iban: row.iban ?? undefined,
    ustIdnr: row.ust_idnr ?? undefined,
    steuernummer: row.steuernummer ?? undefined,
    datevKreditorNr: row.datev_kreditor_nr ?? undefined,
    status: row.status,
    rating: row.rating != null ? Number(row.rating) : undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
});

const mapQualiRow = (row: any): FreelancerQualification => ({
    id: row.id,
    freelancerId: row.freelancer_id,
    typ: row.typ,
    bezeichnung: row.bezeichnung ?? undefined,
    gueltigBis: row.gueltig_bis ?? undefined,
    dokumentPfad: row.dokument_pfad ?? undefined,
});

// ─── Eigener Stammsatz ───────────────────────────────────────────────────────

/**
 * Der eigene Freelancer-Stammsatz.
 *
 * `null` heisst: dieser Login ist mit keiner Zeile verknuepft
 * (`profile_user_id` noch nicht gesetzt). Das ist KEIN Fehler, sondern der
 * Zustand unmittelbar nach der Rollenvergabe — das Portal zeigt dafuer
 * „Ihr Zugang ist noch nicht freigeschaltet" (portalZugang).
 *
 * `maybeSingle()` statt `single()`: `single()` wuerde bei null Treffern einen
 * Fehler werfen und genau diesen Normalfall in einen Ladefehler verwandeln.
 */
export const fetchMeinFreelancer = async (): Promise<Freelancer | null> => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;

    const { data, error } = await supabase
        .from('freelancers')
        .select('*')
        .eq('profile_user_id', userId)
        .maybeSingle();
    if (error) throw new Error(describeError(error));
    return data ? mapFreelancerRow(data) : null;
};

/** Die eigenen Qualifikationsnachweise — im Portal read-only (Ampel). */
export const fetchMeineQualifikationen = async (
    freelancerId: string,
): Promise<FreelancerQualification[]> => {
    if (!freelancerId) return [];
    const { data, error } = await supabase
        .from('freelancer_qualifications')
        .select('*')
        .eq('freelancer_id', freelancerId)
        .order('typ', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapQualiRow);
};

// ─── Eigene Auftraege ────────────────────────────────────────────────────────

/**
 * Alle eigenen Begehungsauftraege.
 *
 * Der `freelancer_id`-Filter ist BEQUEMLICHKEIT (weniger Zeilen ueber die
 * Leitung), nicht Sicherheit — die RLS liefert einem Freelancer ohnehin nur
 * seine eigenen. Ohne Stammsatz gibt es nichts zu laden.
 */
export const fetchMeineAuftraege = async (
    freelancerId: string,
): Promise<InspectionAssignment[]> => {
    if (!freelancerId) return [];
    const { data, error } = await supabase
        .from('inspection_assignments')
        .select('*')
        .eq('freelancer_id', freelancerId)
        .order('zeitraum_von', { ascending: false });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapAssignmentRow);
};

/**
 * Statuswechsel durch den Freelancer — annehmen oder ablehnen.
 *
 * Es gibt bewusst keinen Direkt-Schreibweg daneben: `set_assignment_status`
 * ist die einzige Stelle, an der sich `status` fachlich aendern darf (Plan 3.4,
 * Befund 3.6). Welche Ziele die Oberflaeche ueberhaupt anbietet, entscheidet
 * `freelancerAktionen`; die Datenbank entscheidet es verbindlich noch einmal.
 */
export const setzeAuftragsStatus = async (
    assignmentId: string,
    status: AssignmentStatus,
): Promise<AssignmentStatus> => {
    const { data, error } = await supabase.rpc('set_assignment_status', {
        p_assignment: assignmentId,
        p_new: status,
    });
    if (error) throw new Error(describeError(error));
    return (String(data || status) as AssignmentStatus);
};

// ─── Eigene Projekte ─────────────────────────────────────────────────────────

/**
 * Die freigeschalteten Projekte.
 *
 * ─── AB D4 ueber eine RPC, nicht mehr ueber die View ────────────────────────
 * Bis D3 las diese Funktion `freelancer_projects_v`. Die View laeuft mit
 * `security_invoker = on` und stuetzte sich damit auf eine permissive
 * SELECT-Policy auf `public.projects` — und die machte `projects.data` fuer
 * freigeschaltete Projekte per Direktaufruf lesbar (dokumentiertes Restrisiko
 * in freelancer_portal.sql, Abschnitt 4b).
 *
 * supabase/freelancer_inspection.sql (MIGRATIONS.md Nr. 21, Abschnitt 6) nimmt
 * diese Policy zurueck und liefert dieselben fuenf Spalten ueber die
 * SECURITY-DEFINER-RPC `freelancer_list_projects()`. Fuer einen Freelancer
 * liefert die View danach NULL Zeilen; ein Rueckfallweg auf sie waere deshalb
 * keiner. Die Konstanten PORTAL_PROJEKT_VIEW/-SPALTEN bleiben stehen: sie
 * dokumentieren die Spaltenauswahl, auf die sich die RPC stuetzt, und die View
 * ist als INTERNE Sicht weiterhin gueltig.
 */
export const fetchMeineProjekte = async (): Promise<PortalProjekt[]> => {
    const { data, error } = await supabase.rpc('freelancer_list_projects');
    if (error) throw new Error(describeError(error));
    return (Array.isArray(data) ? data : []).map(mapProjektRow);
};

// ─── Begehung: laden, speichern, einreichen (D4) ─────────────────────────────

/**
 * Alle Daten EINER Begehung.
 *
 * Kein einziger Direktzugriff auf `projects` oder eine der acht Kindtabellen:
 * die RPC prueft „eigener Auftrag, in Bearbeitung, freigeschaltet, Zeitraum
 * begonnen" im Rumpf und liefert eine feste Spaltenauswahl. Fuer die Rolle
 * `freelancer` gibt es auf diesen Tabellen weiterhin keine Lese-Policy —
 * genau das ist der Punkt (Plan 3.4, Befund 3.3).
 *
 * `null` heisst „die Antwort war leer", nicht „kein Zugriff": ein
 * unberechtigter Aufruf wirft eine Exception mit deutschem Klartext.
 */
export const ladeBegehung = async (
    assignmentId: string,
): Promise<FreelancerInspectionData | null> => {
    if (!assignmentId) return null;
    const { data, error } = await supabase.rpc('freelancer_load_inspection', {
        p_assignment: assignmentId,
    });
    if (error) throw new Error(describeError(error));
    return mapInspectionResponse(data);
};

/**
 * Checklistenstaende und Maengel zurueckschreiben.
 *
 * Die RPC schreibt in DIESELBEN Tabellen wie der interne Speicherpfad
 * (project_checklists, inspection_items) — der interne Pruefer und der
 * bestehende Berichts-/PDF-Fluss sehen die Daten danach ganz normal. Sie loescht
 * nichts (kein Prune wie in save_project_children_tx), damit ein Client mit
 * unvollstaendigem Stand keine interne Arbeit entfernen kann.
 */
export const speichereBegehung = async (
    assignmentId: string,
    payload: InspectionPayload,
): Promise<{ checklists: number; items: number }> => {
    const { data, error } = await supabase.rpc('freelancer_save_inspection', {
        p_assignment: assignmentId,
        p_payload: payload,
    });
    if (error) throw new Error(describeError(error));
    return {
        checklists: Number((data as any)?.checklists ?? 0),
        items: Number((data as any)?.items ?? 0),
    };
};

/**
 * Ein Begehungsfoto in den AUFTRAGSPFAD hochladen.
 *
 * `uploadAsset` aus cloudService wird unveraendert wiederverwendet — es macht
 * genau das Richtige (base64 -> Blob, Hash als Dateiname, Wiederholversuche)
 * und haengt den Namen an den uebergebenen Ordner. Der Ordner ist hier NICHT
 * die Projekt-uuid wie im internen Pfad, sondern
 * `<project_id>/freelancer/<assignment_id>` (inspectionFotoPfad) — das ist das
 * einzige Praefix, fuer das die Storage-Policy
 * `project_assets_freelancer_insert` ein INSERT zulaesst.
 *
 * Scheitert der Upload dreimal, liefert `uploadAsset` den base64-String
 * zurueck. Das ist im internen Pfad ein geduldeter Rueckfall (Zeile wird gross,
 * aber nichts geht verloren) — hier nicht: `freelancer_save_inspection` weist
 * `data:`-Fotos ab. Deshalb wird der Fall hier erkannt und als Fehler gemeldet,
 * statt ihn bis zum Speichern zu tragen.
 */
export const ladeBegehungsFotoHoch = async (
    base64: string,
    projectId: string,
    assignmentId: string,
): Promise<string> => {
    const url = await uploadAsset(base64, inspectionFotoPfad(projectId, assignmentId));
    if (url.startsWith('data:')) {
        throw new Error(
            'Das Foto liess sich nicht hochladen. Bitte die Verbindung prüfen und erneut '
            + 'versuchen — ohne Upload lässt sich der Mangel nicht speichern.',
        );
    }
    return url;
};

// ─── Admin-Seite: Portal-Zugang verknuepfen ─────────────────────────────────

export interface PortalKandidat {
    id: string;
    email: string;
    name: string;
}

/**
 * Profile mit der Rolle `freelancer`, die noch mit KEINEM Stammsatz verknuepft
 * sind — die Auswahlliste im Feld „Portal-Zugang" der Freelancer-Akte.
 *
 * Zwei Abfragen statt eines Joins: PostgREST kennt kein `not exists` ueber
 * eine zweite Tabelle, und `not.in.(…)` braucht die Liste ohnehin im Client.
 * Bei der zu erwartenden Groessenordnung (eine Handvoll Freelancer-Logins) ist
 * das die einfachere und ehrlichere Loesung.
 */
export const fetchPortalKandidaten = async (
    /** Diese Verknuepfung nicht ausblenden — die aktuell gesetzte. */
    behalte?: string,
): Promise<PortalKandidat[]> => {
    const { data, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .eq('role', 'freelancer');
    if (error) throw new Error(describeError(error));

    const { data: belegt, error: belegtError } = await supabase
        .from('freelancers')
        .select('profile_user_id')
        .not('profile_user_id', 'is', null);
    if (belegtError) throw new Error(describeError(belegtError));

    const vergeben = new Set(
        (belegt || []).map((r: any) => String(r.profile_user_id)).filter((id) => id !== behalte),
    );

    return (data || [])
        .map((row: any) => ({
            id: String(row.id),
            email: String(row.email || ''),
            name: [row.first_name, row.last_name].filter(Boolean).join(' ').trim(),
        }))
        .filter((k) => !vergeben.has(k.id))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'de'));
};

/**
 * Portal-Zugang setzen oder loesen (Innendienst).
 *
 * BEWUSST NICHT in freelancerService.updateFreelancer: `toFreelancerRow` laesst
 * `profileUserId` mit Begruendung aus (ein Stammdatenformular darf keinen Login
 * freischalten). Diese Funktion ist der eine, benannte Weg — und sie ist der
 * gefaehrlichste Schreibvorgang des Moduls: die falsche uuid hier gibt einem
 * fremden Menschen Zugriff auf die Auftraege dieses Freelancers.
 *
 * `null` loest die Verknuepfung. Der Zugang ist danach sofort wirkungslos: alle
 * Freelancer-Policies haengen an `profile_user_id = auth.uid()`.
 */
export const setzePortalZugang = async (
    freelancerId: string,
    profileUserId: string | null,
): Promise<Freelancer | null> => {
    if (!freelancerId) return null;
    const { data, error } = await supabase
        .from('freelancers')
        .update({ profile_user_id: profileUserId })
        .eq('id', freelancerId)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapFreelancerRow(data);
};
